import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ServiceTemplate } from './service-template.model';
import { ServiceTemplateInput, ServiceTemplateStorage } from './service-template-storage';

@Injectable({
    providedIn: 'root',
})
export class ServiceTemplateService {
    private templatesSubject = new BehaviorSubject<ServiceTemplate[]>([]);
    public templates$ = this.templatesSubject.asObservable();
    private loaded = false;

    constructor(private storage: ServiceTemplateStorage) {}

    /** Loads templates from storage into memory (no-op after the first call unless forced). */
    async loadTemplates(force = false): Promise<ServiceTemplate[]> {
        if (!this.loaded || force) {
            const templates = await this.storage.getAll();
            this.templatesSubject.next(templates);
            this.loaded = true;
        }
        return this.templatesSubject.value;
    }

    getTemplates(): ServiceTemplate[] {
        return this.templatesSubject.value;
    }

    getTemplate(id: string): ServiceTemplate | undefined {
        return this.templatesSubject.value.find((t) => t.id === id);
    }

    async createTemplate(template: ServiceTemplateInput): Promise<ServiceTemplate> {
        const created = await this.storage.create(template);
        this.templatesSubject.next([...this.templatesSubject.value, created]);
        return created;
    }

    async updateTemplate(id: string, updates: Partial<ServiceTemplate>): Promise<ServiceTemplate | null> {
        const templates = [...this.templatesSubject.value];
        const index = templates.findIndex((t) => t.id === id);

        if (index === -1) {
            return null;
        }

        // Merge into the known template so the storage receives a complete
        // editable payload (the API replaces `data` wholesale on update).
        const merged: ServiceTemplate = {
            ...templates[index],
            ...updates,
            id: templates[index].id,
            createdAt: templates[index].createdAt,
            updatedAt: templates[index].updatedAt,
        };

        const saved = await this.storage.update(id, this.toInput(merged));
        templates[index] = saved;
        this.templatesSubject.next(templates);
        return saved;
    }

    async deleteTemplate(id: string): Promise<boolean> {
        const templates = this.templatesSubject.value;
        const exists = templates.some((t) => t.id === id);

        if (!exists) {
            return false;
        }

        await this.storage.delete(id);
        this.templatesSubject.next(templates.filter((t) => t.id !== id));
        return true;
    }

    private toInput(template: ServiceTemplate): ServiceTemplateInput {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, createdAt, updatedAt, ...input } = template;
        return input;
    }
}
